import shallow from 'zustand/shallow';
import { useStore } from '../../../../../store/useStore';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { useState, useMemo, useCallback } from 'react';
import { useToggle } from '../../../../../hooks/useToggle';

export const useUserCapacity = (user: TimeUser) => {
    const { availability: userCapacity, calendar, id: userId } = user;
    const calendarCapacity = calendar?.dailyCapacity;
    const {
        initialData: {
            configuration: { capacityDefault },
        },
    } = useConfigValuesState();
    const { currentUsers, setCurrentUsers } = useStore(
        store => ({
            currentUsers: store.currentUsers,
            setCurrentUsers: store.setCurrentUsers,
        }),
        shallow,
    );

    const [isInEditMode, toggleEditMode] = useToggle(false);

    // Priority system: 1. explicit capacity, 2. calendar capacity, 3. global default
    const effectiveCapacity = useMemo(() => {
        if (userCapacity !== null && userCapacity !== undefined) {
            return userCapacity; // Priority 1: explicit user capacity
        }
        if (calendarCapacity !== null && calendarCapacity !== undefined) {
            return calendarCapacity; // Priority 2: calendar capacity
        }
        return capacityDefault; // Priority 3: global default
    }, [userCapacity, calendarCapacity, capacityDefault]);

    // Determine the source of the capacity value for UI display
    const hasExplicitUserCapacity = userCapacity !== null && userCapacity !== undefined;
    const hasCalendarCapacity = calendarCapacity !== null && calendarCapacity !== undefined;

    const isDefault = useMemo(() => {
        return !hasExplicitUserCapacity && !hasCalendarCapacity;
    }, [hasExplicitUserCapacity, hasCalendarCapacity]);

    const isCalendar = useMemo(() => {
        return !hasExplicitUserCapacity && hasCalendarCapacity;
    }, [hasExplicitUserCapacity, hasCalendarCapacity]);
    const activeCapacity = useMemo(() => String(effectiveCapacity), [effectiveCapacity]);
    const [capacityInputValue, setCapacityInputValue] = useState<string>(activeCapacity);

    const updateUserCapacity = useCallback(
        (availability: number | null) => {
            setCurrentUsers(currentUsers.map(currentUser => (currentUser.id === userId ? { ...currentUser, availability } : currentUser)));
        },
        [currentUsers, setCurrentUsers, userId],
    );

    const resetToDefault = useCallback(() => {
        updateUserCapacity(null);
        // After resetting user capacity, the effective capacity will fall back to calendar or default
        const fallbackCapacity = calendarCapacity ?? capacityDefault;
        setCapacityInputValue(String(fallbackCapacity));
    }, [updateUserCapacity, calendarCapacity, capacityDefault]);

    // Handle input change but allow free typing
    const onCapacityChange = useCallback((_: any, value: string) => {
        setCapacityInputValue(value);
    }, []);

    // Validate input on blur and update state
    const onCapacityBlur = useCallback(() => {
        const parsedNumber = Number(capacityInputValue);
        const isCapacityValid = !isNaN(parsedNumber) && parsedNumber >= 0 && parsedNumber <= 24;
        if (isCapacityValid) {
            updateUserCapacity(parsedNumber);
        } else {
            setCapacityInputValue(activeCapacity);
        }
        toggleEditMode();
    }, [capacityInputValue, activeCapacity, updateUserCapacity, toggleEditMode]);

    return {
        isInEditMode,
        toggleEditMode,
        resetToDefault,
        activeCapacity,
        isDefault,
        isCalendar,
        updateUserCapacity,
        capacityInputValue,
        onCapacityChange,
        onCapacityBlur,
    };
};
